/**
 * Amazon Ads — Sponsored Products v3 Reports Module (Story 19.14, Task 4)
 *
 * Implements the submit / poll / download / parse / cancel orchestration for
 * Amazon Ads v3 async reports.
 *
 * Architecture:
 *   - POST /reporting/reports (Content-Type: application/vnd.createasyncreportrequest.v3+json)
 *   - GET  /reporting/reports/{reportId} (status polling)
 *   - GET  <pre-signed S3 URL from response.url> (gzipped NDJSON download)
 *   - DELETE /reporting/reports/{reportId} (cancellation, used by reaper + dry-run probe)
 *
 * Defensive layers:
 *   - SSRF allowlist regex (Round 3 Vuln 1)
 *   - 50MB decompressed-stream cap (Round 3 Vuln 2)
 *   - Line-by-line NDJSON parse with malformed-line tolerance (AC #4)
 *   - Cache-payload validation against XSS payload + 100k record cap (Round 3 Vuln 4)
 *   - SHA-256 request hash with normalized body (AC #5)
 */

import {
  AMAZON_ADS_REGIONAL_HOSTS,
  type AmazonAdsRegion,
} from "./oauth";
import {
  buildColumnList,
  buildGroupByList,
  pickReportType,
  SP_REPORT_TYPE_IDS,
  type SpReportTypeId,
} from "./sp-column-map";
import {
  buildSbColumnList,
  buildSbGroupByList,
  pickSbReportType,
  SB_REPORT_TYPE_IDS,
  type SbReportTypeId,
} from "./sb-column-map";
import {
  buildSdColumnList,
  buildSdGroupByList,
  pickSdReportType,
  SD_REPORT_TYPE_IDS,
  type SdReportTypeId,
} from "./sd-column-map";
import {
  buildDspColumnList,
  buildDspGroupByList,
  pickDspReportType,
  DSP_REPORT_TYPE_IDS,
  type DspReportTypeId,
} from "./dsp-column-map";

// ============================================================================
// Constants (AC #2, #3, #5)
// ============================================================================

/** AC #7 — fixed 31-day chunks (NOT month-aligned). */
export const AMAZON_ADS_SP_MAX_DAYS_PER_REPORT = 31;

/** AC #2 — wall-clock budget for a single fetchMetrics invocation. */
export const AMAZON_ADS_SP_REPORT_TIMEOUT_MS = 30_000;

/** AC #13 — S3 download timeout (longer because S3 sometimes serves slow first byte). */
export const AMAZON_ADS_SP_DOWNLOAD_TIMEOUT_MS = 25_000;

/** AC #13 — control-plane (POST/GET/DELETE) timeout. 8s with 16x safety margin. */
export const AMAZON_ADS_SP_CONTROL_TIMEOUT_MS = 8_000;

/** Round 3 Vuln 2 — decompression-bomb defense. */
export const AMAZON_ADS_SP_MAX_DECOMPRESSED_BYTES = 50 * 1024 * 1024;

/** AC #5 — 12-row safeguard below Amazon's 15-concurrent hard cap. */
export const AMAZON_ADS_SP_MAX_REPORTS_PER_PROFILE = 12;

/** AC #1 — v3 request content-type. Plain `application/json` yields HTTP 415. */
export const AMAZON_ADS_REPORT_API_CONTENT_TYPE =
  "application/vnd.createasyncreportrequest.v3+json";

/**
 * Story 19.16 Task 5.1 / AC #2 — DSP-specific v3 request content-type. DSP
 * REQUIRES `application/vnd.dspcreatereports.v3+json` on BOTH `Content-Type`
 * AND `Accept`. Reusing the SP/SB/SD content-type returns HTTP 415 Unsupported
 * Media Type per amzn/ads-advanced-tools-docs Discussion #15. This is the
 * single biggest header divergence between DSP and SP/SB/SD.
 */
export const AMAZON_ADS_DSP_REPORT_API_CONTENT_TYPE =
  "application/vnd.dspcreatereports.v3+json";

/** Round 3 Vuln 1 — S3 hostname allowlist (SP/SB/SD — broad). */
export const AMAZON_ADS_S3_HOSTNAME_REGEX =
  /^[a-z0-9-]+\.s3(\.[a-z0-9-]+)?\.amazonaws\.com$/;

/**
 * Story 19.16 Task 5.2 / AC #10 — DSP-specific S3 hostname allowlist (TIGHTER
 * than SP/SB/SD's broad regex). DSP downloads come from `corvo-reports.s3.amazonaws.com`
 * specifically per the Python Amazon Ad API SDK docs. The tighter regex is
 * defense-in-depth — it would block any future Amazon-side bucket-name-spoof
 * attack at zero cost (Amazon ALWAYS uses the corvo-reports bucket for DSP).
 */
export const AMAZON_ADS_DSP_S3_HOSTNAME_REGEX =
  /^corvo-reports\.s3(\.[a-z0-9-]+)?\.amazonaws\.com$/;

/** Round 3 Vuln 4 — cache-payload max records (Worker memory protection). */
export const AMAZON_ADS_SP_CACHE_MAX_RECORDS = 100_000;

/** AC #2 — polling cadence sequence in milliseconds, total 31s (skip last on 30s budget). */
export const AMAZON_ADS_SP_POLL_CADENCE_MS: ReadonlyArray<number> = [
  1_000, 2_000, 4_000, 8_000, 8_000, 8_000,
];

/** v3 status enum + v2 alias acceptance per AC #2. */
export const AMAZON_ADS_SP_TERMINAL_SUCCESS_STATUSES: ReadonlyArray<string> = [
  "COMPLETED",
  "SUCCESS", // v2-alias defensive accept
];

export const AMAZON_ADS_SP_PENDING_STATUSES: ReadonlyArray<string> = [
  "PENDING",
  "IN_QUEUE",
  "PROCESSING",
];

// ============================================================================
// Types
// ============================================================================

export interface SpReportConfig {
  readonly adProduct: "SPONSORED_PRODUCTS";
  readonly reportTypeId: SpReportTypeId;
  readonly groupBy: ReadonlyArray<string>;
  readonly columns: ReadonlyArray<string>;
  readonly timeUnit: "DAILY" | "SUMMARY";
  readonly format: "GZIP_JSON";
}

export interface SpReportRequestBody {
  readonly name: string;
  readonly startDate: string; // YYYY-MM-DD
  readonly endDate: string; // YYYY-MM-DD
  readonly configuration: SpReportConfig;
}

export interface SpSubmitReportArgs {
  readonly accessToken: string;
  readonly clientId: string; // LWA client ID
  readonly profileId: string;
  readonly region: AmazonAdsRegion;
  readonly body: SpReportRequestBody;
}

export interface SpSubmitReportResult {
  readonly reportId: string;
  readonly status: string;
}

export interface SpPollReportResult {
  readonly status: string;
  readonly url?: string;
  readonly failureReason?: string;
}

export type SpReportRecord = Record<string, unknown>;

export type SpParseLineResult =
  | { readonly _malformed: true; readonly reason: string }
  | { readonly _malformed: false; readonly record: SpReportRecord };

// ============================================================================
// Request body building (AC #1)
// ============================================================================

export interface BuildSpReportRequestArgs {
  readonly reportTypeId: SpReportTypeId;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly metrics: ReadonlyArray<string>;
  readonly dimensions: ReadonlyArray<string>;
  readonly timeUnit?: "DAILY" | "SUMMARY";
  /** First 8 hex chars of request_hash — embedded in the report `name` for grep-ability. */
  readonly requestHashShort?: string;
}

export function buildSpReportRequest(
  args: BuildSpReportRequestArgs
): { body: SpReportRequestBody; warnings: string[] } {
  const startDate = isoDate(args.startDate);
  const endDate = isoDate(args.endDate);
  const { columns, warnings } = buildColumnList(
    args.reportTypeId,
    args.metrics,
    args.dimensions
  );
  const groupBy = buildGroupByList(args.reportTypeId, args.dimensions);
  const name = `sp-${args.reportTypeId}-${startDate}-${endDate}-${args.requestHashShort ?? "noh"}`;

  const body: SpReportRequestBody = {
    name,
    startDate,
    endDate,
    configuration: {
      adProduct: "SPONSORED_PRODUCTS",
      reportTypeId: args.reportTypeId,
      groupBy,
      columns,
      timeUnit: args.timeUnit ?? "DAILY",
      format: "GZIP_JSON",
    },
  };
  return { body, warnings };
}

function isoDate(d: Date): string {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  )
    .toISOString()
    .slice(0, 10);
}

// ============================================================================
// Request normalization + hashing (AC #5, #10)
// ============================================================================

/**
 * Stable JSON normalization of the v3 request body for cache keys + request-hash
 * deduplication. Drops the non-deterministic `name` field and sorts every array
 * + object key recursively.
 *
 * Story 19.15 — accepts any of SP/SB/SD bodies. The `configuration` object's
 * `adProduct` + `reportTypeId` automatically participate in the hash via
 * `canonicalize`, so SP/SB/SD requests with the same date range produce 3
 * distinct hashes (per AC #7's request-hash dedup invariant).
 */
export function normalizeRequestForHash(
  body: SpReportRequestBody | AnyAmazonAdsReportRequestBody
): string {
  return JSON.stringify(canonicalize(stripName(body)));
}

function stripName(b: AnyAmazonAdsReportRequestBody): Record<string, unknown> {
  // Story 19.16 — SP/SB/SD bodies carry a non-deterministic `name` field that
  // must be excluded from the hash. DSP bodies have no `name` field at all,
  // so the strip is a no-op there.
  if ("name" in b) {
    const { name: _name, ...rest } = b;
    return rest;
  }
  return { ...b };
}

function canonicalize(v: unknown): unknown {
  if (v === null || typeof v !== "object") return v;
  if (Array.isArray(v)) {
    // Sort array elements for arrays of strings (columns, groupBy, metrics, dims).
    if (v.every((x) => typeof x === "string")) {
      return [...(v as string[])].sort();
    }
    return v.map(canonicalize);
  }
  const obj = v as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(obj).sort()) {
    sorted[k] = canonicalize(obj[k]);
  }
  return sorted;
}

/**
 * SHA-256 hash of a normalized request body, truncated to 32 hex chars to keep
 * KV-key length within budget.
 */
export async function computeRequestHash(
  normalizedBody: string
): Promise<string> {
  const data = new TextEncoder().encode(normalizedBody);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(digest);
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex.slice(0, 32);
}

// ============================================================================
// SSRF allowlist (AC #3 / Round 3 Vuln 1)
// ============================================================================

export function isSafeS3DownloadUrl(
  url: string,
  adProduct?: AmazonAdsAdProduct
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  if (parsed.port !== "") return false; // No non-standard ports allowed.

  // Story 19.16 Task 5.3 / AC #10 — DSP uses the tighter `corvo-reports.s3*` regex.
  // SP/SB/SD (or undefined for back-compat with 19.14 callers) uses the broad
  // `*.s3*.amazonaws.com` regex. This dispatch is purely additive; existing
  // 2-arg call sites without `adProduct` continue to use the broad allowlist.
  if (adProduct === "DSP") {
    if (!AMAZON_ADS_DSP_S3_HOSTNAME_REGEX.test(parsed.hostname)) return false;
    return true;
  }
  if (!AMAZON_ADS_S3_HOSTNAME_REGEX.test(parsed.hostname)) return false;
  return true;
}

// ============================================================================
// NDJSON line parsing (AC #4)
// ============================================================================

export function parseSpNdjsonRecord(line: string): SpParseLineResult {
  const trimmed = line.trim();
  if (trimmed.length === 0) {
    return { _malformed: true, reason: "empty line" };
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { _malformed: true, reason: "not a JSON object" };
    }
    return { _malformed: false, record: parsed as SpReportRecord };
  } catch (err) {
    return {
      _malformed: true,
      reason: err instanceof Error ? err.message : "JSON parse error",
    };
  }
}

// ============================================================================
// Cache-payload validation (Round 3 Vuln 4)
// ============================================================================

export interface ValidateCachePayloadResult {
  readonly valid: boolean;
  readonly reason?: string;
}

const SCRIPT_TAG_RE = /<\s*script/i;
const JS_URL_RE = /^\s*javascript\s*:/i;

export function validateCachePayload(
  records: ReadonlyArray<SpReportRecord>
): ValidateCachePayloadResult {
  if (!Array.isArray(records)) {
    return { valid: false, reason: "records must be an array" };
  }
  if (records.length > AMAZON_ADS_SP_CACHE_MAX_RECORDS) {
    return {
      valid: false,
      reason: `array exceeds ${AMAZON_ADS_SP_CACHE_MAX_RECORDS} record cap`,
    };
  }
  for (const r of records) {
    if (r === null || typeof r !== "object" || Array.isArray(r)) {
      return { valid: false, reason: "non-object record" };
    }
    for (const v of Object.values(r)) {
      if (typeof v === "string") {
        if (SCRIPT_TAG_RE.test(v)) {
          return { valid: false, reason: "string field contains <script" };
        }
        if (JS_URL_RE.test(v)) {
          return {
            valid: false,
            reason: "string field contains javascript: URL",
          };
        }
      }
    }
  }
  return { valid: true };
}

// ============================================================================
// HTTP — submit / poll / download / cancel
// ============================================================================

export class SpReportApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly providerBody?: string
  ) {
    super(message);
    this.name = "SpReportApiError";
  }
}

/**
 * AC #11 — map an `SpReportApiError` to an `AdapterErrorCode` + structured
 * event name. Encodes the 7 distinct rules from the AC:
 *
 *   - `400` + body matches `/invalid date range/i` or `/exceeds.*max/i` → `invalid_request`
 *   - `400` + body matches `/invalid column/i` or `/unknown groupBy/i` → `invalid_request`
 *     + emit `amazon_ads_invalid_report_config`
 *   - `400` + body matches `/in-flight reports/i` or `/concurrent.*exceeded/i` →
 *     `rate_limited` retryable + emit `amazon_ads_safeguard_breach`
 *   - `400` + body matches `/invalid_client/i` (LWA-shaped error in report path) →
 *     `token_invalid` (deployment_misconfig) — Story 19.13 AC #15 carry-forward
 *   - `401` + body matches `/unknown_scope|api_access_not_assigned/` → `api_not_enabled`,
 *     else → `token_expired`
 *   - `403` + body matches `api access patterns` → `api_not_enabled`, else → `permission_denied`
 *   - `404` → `invalid_response`
 *   - `429` → `rate_limited` (Retry-After parsing handled by caller)
 *   - `5xx` → `provider_unavailable` retryable
 */
export interface ParsedSpReportError {
  readonly code:
    | "invalid_response"
    | "rate_limited"
    | "permission_denied"
    | "api_not_enabled"
    | "provider_unavailable"
    | "token_expired"
    | "token_invalid"
    | "brand_registry_required"
    | "dsp_not_enabled";
  readonly retryable: boolean;
  readonly logEvent?: string;
}

/**
 * Story 19.15 AC #6 — parses Amazon's error envelope into an adapter-domain
 * code. Surface-aware: SB-specific 4xx with body matching `/preview/i` OR
 * `/multiAdGroup/i` → `brand_registry_required` (NOT `api_not_enabled` —
 * different remediation path: customer enrolls at amazon.com/brandregistry).
 * SD-specific 4xx with body matching `/beta/i` → `provider_unavailable`
 * retryable + emits `amazon_ads_sd_v3_beta_observed` log.
 */
export function parseSpReportError(
  err: SpReportApiError,
  adProduct?: AmazonAdsAdProduct
): ParsedSpReportError {
  const status = err.status;
  const body = err.providerBody ?? "";

  if (status >= 400 && status < 500) {
    // Story 19.16 AC #6 — DSP-not-enabled distinct mapping. Distinct from
    // `api_not_enabled` (LWA scope) and `permission_denied` (per-resource);
    // remediation is contacting an Amazon DSP sales rep. Integration stays
    // `healthy` when SP/SB/SD work but DSP returns this.
    if (
      adProduct === "DSP" &&
      (/dsp.*not.*enabled/i.test(body) || /no.*dsp.*account/i.test(body))
    ) {
      return {
        code: "dsp_not_enabled",
        retryable: false,
        logEvent: "amazon_ads_partial_surface_unavailable",
      };
    }
    // Story 19.15 AC #6 — SB Brand Registry distinct mapping (Round 2 Pre-mortem A).
    if (
      adProduct === "SPONSORED_BRANDS" &&
      (/preview/i.test(body) || /multiAdGroup/i.test(body))
    ) {
      return {
        code: "brand_registry_required",
        retryable: false,
        logEvent: "amazon_ads_brand_registry_required",
      };
    }
    // Story 19.15 AC #6 — SD v3 beta status (Round 2 Pre-mortem B).
    if (adProduct === "SPONSORED_DISPLAY" && /beta/i.test(body)) {
      return {
        code: "provider_unavailable",
        retryable: true,
        logEvent: "amazon_ads_sd_v3_beta_observed",
      };
    }
  }

  if (status === 400) {
    if (/invalid_client/i.test(body)) {
      // AC #15 carry-forward (Story 19.13) — single highest-blast-radius
      // mapping. NEVER token_expired; treats as deployment_misconfig.
      return { code: "token_invalid", retryable: true };
    }
    if (
      /in-flight reports/i.test(body) ||
      /concurrent.*exceeded/i.test(body)
    ) {
      return {
        code: "rate_limited",
        retryable: true,
        logEvent: "amazon_ads_safeguard_breach",
      };
    }
    if (/invalid column/i.test(body) || /unknown groupBy/i.test(body)) {
      return {
        code: "invalid_response",
        retryable: false,
        logEvent: "amazon_ads_invalid_report_config",
      };
    }
    if (/invalid date range/i.test(body) || /exceeds.*max/i.test(body)) {
      return { code: "invalid_response", retryable: false };
    }
    return { code: "invalid_response", retryable: false };
  }

  if (status === 401) {
    if (/unknown_scope/i.test(body) || /api_access_not_assigned/i.test(body)) {
      return { code: "api_not_enabled", retryable: false };
    }
    return { code: "token_expired", retryable: false };
  }
  if (status === 403) {
    if (/access/i.test(body) && /not/i.test(body)) {
      return { code: "api_not_enabled", retryable: false };
    }
    return { code: "permission_denied", retryable: false };
  }
  if (status === 404) {
    return { code: "invalid_response", retryable: false };
  }
  if (status === 429) {
    return { code: "rate_limited", retryable: true };
  }
  if (status >= 500 && status < 600) {
    return { code: "provider_unavailable", retryable: true };
  }
  // Default fallback for unexpected status codes.
  return { code: "invalid_response", retryable: false };
}

/**
 * Story 19.15 AC #6 — alias for `parseSpReportError` with the more general
 * name reflecting multi-product support. Carries forward the same 19.14
 * behavior for SP, adds SB/SD-aware mappings when the optional `adProduct`
 * is supplied.
 *
 * NOTE: distinct from `parseAmazonAdsApiError` in `./errors.ts` which parses
 * OAuth-shaped error envelopes (different surface — tokens vs reports). Keep
 * them as separate symbols.
 */
export const parseAmazonAdsReportApiError = parseSpReportError;

function buildAmazonHeaders(args: {
  accessToken: string;
  clientId: string;
  profileId: string;
}): Record<string, string> {
  return {
    // Story 19.13 AC #4: lowercase `bearer ` prefix.
    Authorization: `bearer ${args.accessToken}`,
    "Amazon-Advertising-API-ClientId": args.clientId,
    "Amazon-Advertising-API-Scope": args.profileId,
  };
}

/**
 * Story 19.14 — original SP submit. **As of Story 19.15, this is a deprecated
 * thin wrapper around `submitAmazonAdsReport` (AC #2 rename). Defers all the
 * defensive layers (AC #11 runtime guard, content-type, scope headers,
 * abort-controller timeout) to the canonical implementation. The deprecated
 * alias is removed in Story 19.17.**
 *
 * @deprecated Story 19.15 — use `submitAmazonAdsReport` directly.
 */
export async function submitSponsoredProductsReport(
  args: SpSubmitReportArgs,
  fetchImpl: typeof fetch = fetch
): Promise<SpSubmitReportResult> {
  return submitAmazonAdsReport(args, fetchImpl);
}

export async function pollReport(
  args: {
    accessToken: string;
    clientId: string;
    profileId: string;
    region: AmazonAdsRegion;
    reportId: string;
    /**
     * Story 19.16 Round 1 review fix — when supplied, the poll URL uses the
     * DSP-specific path `/accounts/{dspAccountId}/dsp/reports/{reportId}`
     * instead of SP/SB/SD's `/reporting/reports/{reportId}` (per Discussion
     * #15 + Python SDK). When omitted (back-compat for SP/SB/SD callers),
     * the existing path is used. Caller is responsible for setting this
     * when polling DSP reports.
     */
    adProduct?: AmazonAdsAdProduct;
  },
  fetchImpl: typeof fetch = fetch
): Promise<SpPollReportResult> {
  const url =
    args.adProduct === "DSP"
      ? `${AMAZON_ADS_REGIONAL_HOSTS[args.region]}/accounts/${encodeURIComponent(args.profileId)}/dsp/reports/${encodeURIComponent(args.reportId)}`
      : `${AMAZON_ADS_REGIONAL_HOSTS[args.region]}/reporting/reports/${encodeURIComponent(args.reportId)}`;
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    AMAZON_ADS_SP_CONTROL_TIMEOUT_MS
  );
  try {
    const res = await fetchImpl(url, {
      method: "GET",
      headers: buildAmazonHeaders(args),
      redirect: "manual",
      signal: controller.signal,
    });
    if (!res.ok) {
      const txt = await safeReadText(res);
      throw new SpReportApiError(res.status, `poll report: HTTP ${res.status}`, txt);
    }
    const json = (await res.json()) as {
      status?: string;
      url?: string;
      // The "url" field name is the v3 contract per
      // advertising.amazon.com/API/docs/en-us/reporting/v3/get-started.
      // Defensive: also accept "location" for forward-compat with practitioner
      // gists noting older shapes.
      location?: string;
      failureReason?: string;
    };
    return {
      status: json.status ?? "UNKNOWN",
      ...(json.url ?? json.location
        ? { url: json.url ?? json.location }
        : {}),
      ...(json.failureReason !== undefined
        ? { failureReason: json.failureReason }
        : {}),
    };
  } finally {
    clearTimeout(timeout);
  }
}

export async function cancelReport(
  args: {
    accessToken: string;
    clientId: string;
    profileId: string;
    region: AmazonAdsRegion;
    reportId: string;
  },
  fetchImpl: typeof fetch = fetch
): Promise<{ status: number }> {
  const url = `${AMAZON_ADS_REGIONAL_HOSTS[args.region]}/reporting/reports/${encodeURIComponent(args.reportId)}`;
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    AMAZON_ADS_SP_CONTROL_TIMEOUT_MS
  );
  try {
    const res = await fetchImpl(url, {
      method: "DELETE",
      headers: buildAmazonHeaders(args),
      redirect: "manual",
      signal: controller.signal,
    });
    return { status: res.status };
  } finally {
    clearTimeout(timeout);
  }
}

async function safeReadText(res: Response): Promise<string | undefined> {
  try {
    return await res.text();
  } catch {
    return undefined;
  }
}

// ============================================================================
// Download + decompress + parse (AC #3, #4)
// ============================================================================

export interface DownloadAndParseResult {
  readonly records: SpReportRecord[];
  readonly warnings: string[];
  readonly bytesProcessed: number;
}

/**
 * Download the gzipped NDJSON payload from a pre-signed S3 URL, validate the
 * SSRF allowlist, decompress with a 50MB cap, and line-parse with malformed-
 * line tolerance.
 *
 * Throws if the URL fails the SSRF allowlist, exceeds the decompressed-byte
 * cap, or otherwise fails fetch.
 */
export async function downloadAndParseReport(
  url: string,
  fetchImpl: typeof fetch = fetch
): Promise<DownloadAndParseResult> {
  if (!isSafeS3DownloadUrl(url)) {
    throw new Error("suspicious_download_url");
  }

  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    AMAZON_ADS_SP_DOWNLOAD_TIMEOUT_MS
  );
  let res: Response;
  try {
    // No Authorization header — pre-signed URL carries its own auth via signed
    // query params; sending Bearer would 400 from S3.
    res = await fetchImpl(url, {
      method: "GET",
      redirect: "manual",
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    throw new SpReportApiError(res.status, `S3 download HTTP ${res.status}`);
  }

  if (!res.body) {
    throw new Error("S3 response missing body");
  }

  // Stream-decompress with a 50MB cap. `DecompressionStream("gzip")` is
  // available on Cloudflare Workers + modern browsers + Node 18+.
  const decompressed = res.body.pipeThrough(new DecompressionStream("gzip"));
  const reader = decompressed.getReader();
  const decoder = new TextDecoder("utf-8");

  const records: SpReportRecord[] = [];
  const warnings: string[] = [];
  let bytesProcessed = 0;
  let leftover = "";
  let truncatedLineCount = 0;

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (!value) continue;
      bytesProcessed += value.byteLength;
      if (bytesProcessed > AMAZON_ADS_SP_MAX_DECOMPRESSED_BYTES) {
        try {
          reader.releaseLock();
        } catch {
          // best-effort
        }
        throw new Error("report_oversized");
      }
      const chunk = leftover + decoder.decode(value, { stream: true });
      const lines = chunk.split("\n");
      leftover = lines.pop() ?? "";
      for (const line of lines) {
        const r = parseSpNdjsonRecord(line);
        if (r._malformed) {
          truncatedLineCount++;
          continue;
        }
        records.push(r.record);
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }

  // Flush leftover (final line without trailing newline).
  if (leftover.length > 0) {
    const r = parseSpNdjsonRecord(leftover);
    if (r._malformed) {
      truncatedLineCount++;
    } else {
      records.push(r.record);
    }
  }

  if (truncatedLineCount > 0) {
    warnings.push(
      `Skipped ${truncatedLineCount} malformed NDJSON line(s) — adapter is malformed-line-tolerant per AC #4`
    );
  }

  return { records, warnings, bytesProcessed };
}

// ============================================================================
// Column-drift detection (AC #4 + #9)
// ============================================================================

export interface ColumnDriftDetectionResult {
  readonly missingColumns: string[];
  readonly warnings: string[];
}

/**
 * Verify that every requested column appears in at least one record. Missing
 * columns surface as a warning + structured-log event (caller handles the
 * `amazon_ads_column_drift_detected` log).
 */
export function detectColumnDrift(
  requestedColumns: ReadonlyArray<string>,
  records: ReadonlyArray<SpReportRecord>
): ColumnDriftDetectionResult {
  const missingColumns: string[] = [];
  const warnings: string[] = [];
  if (records.length === 0) return { missingColumns, warnings };

  for (const col of requestedColumns) {
    let seen = false;
    for (const r of records) {
      if (Object.prototype.hasOwnProperty.call(r, col)) {
        seen = true;
        break;
      }
    }
    if (!seen) {
      missingColumns.push(col);
      warnings.push(
        `Column drift: ${col} missing from all ${records.length} record(s) — runtime warning per AC #4`
      );
    }
  }
  return { missingColumns, warnings };
}

// ============================================================================
// Derived metrics (AC #4)
// ============================================================================

/**
 * Apply derived metrics (CTR, ACOS, ROAS) to each record. Division-by-zero
 * produces `null` (NOT Infinity / NaN — those break KPI widget rendering).
 *
 * Runs only when the input metrics are present; otherwise leaves the field
 * untouched. ACOS = cost / sales7d; ROAS = sales7d / cost; CTR = clicks /
 * impressions.
 */
export function applyDerivedMetrics(
  records: ReadonlyArray<SpReportRecord>
): SpReportRecord[] {
  return records.map((rec) => {
    const out: SpReportRecord = { ...rec };
    const impressions = numericOrNull(rec.impressions);
    const clicks = numericOrNull(rec.clicks);
    const cost = numericOrNull(rec.cost);
    const sales7d = numericOrNull(rec.sales7d);

    if (out.clickThroughRate === undefined || out.clickThroughRate === null) {
      out.clickThroughRate = safeDivide(clicks, impressions);
    }
    if (out.acosClicks7d === undefined || out.acosClicks7d === null) {
      out.acosClicks7d = safeDivide(cost, sales7d);
    }
    if (out.roasClicks7d === undefined || out.roasClicks7d === null) {
      out.roasClicks7d = safeDivide(sales7d, cost);
    }
    return out;
  });
}

/**
 * Round 3 Vuln 3 numeric guard (Story 19.14) — coerces Amazon-returned values
 * to a number or null. Shared by SP/SB/SD/DSP adapter parse pipelines + DSP
 * derived-metric helpers (Story 35.7 Code Review Round 2 dedup).
 *
 * Round 2 — empty/whitespace strings explicitly map to `null` (NOT 0). Amazon
 * occasionally emits `""` for "no value recorded" on some Lead Gen columns;
 * `Number("")` returns 0, which would corrupt the AC #3 "no Lead Gen campaigns"
 * vs "0 leads recorded" semantic distinction.
 *
 * Usage policy: the canonical call-site is the per-surface parse pipeline
 * (e.g. `parseSdNdjsonRow`, `parseDspJsonRecord`, `applySbDerivedMetrics`).
 * Tests + cross-adapter shared helpers may import directly. This is a leaf
 * utility — adding new top-level callers is fine as long as they're behind
 * the parse-pipeline contract.
 */
export function numericOrNull(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    if (v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function safeDivide(num: number | null, den: number | null): number | null {
  if (num === null || den === null || den === 0) return null;
  return num / den;
}

// ============================================================================
// Dynamic cache TTL (AC #10)
// ============================================================================

export const AMAZON_ADS_SP_CACHE_TTL_FRESH_SEC = 3600; // 1 hour
export const AMAZON_ADS_SP_CACHE_TTL_FINALIZED_SEC = 86_400; // 24 hours

/**
 * Story 19.16 Task 1.1 — Amazon Ads ad-product discriminated union. SP carries
 * forward from 19.14; SB and SD shipped in 19.15; DSP added here. Every
 * `_exhaustive: never` switch arm in `pickAmazonAdsReportType`,
 * `buildAmazonAdsReportRequest`, `buildAmazonAdsCacheKey`, and
 * `validateAdProductReportTypePair` MUST list `case "DSP"` — the typecheck
 * fails loud if a future story adds a 5th surface (e.g. `"AMC"`) without
 * extending the union.
 */
export type AmazonAdsAdProduct =
  | "SPONSORED_PRODUCTS"
  | "SPONSORED_BRANDS"
  | "SPONSORED_DISPLAY"
  | "DSP";

/**
 * Story 19.16 AC #12 — finalisation window per ad-product.
 *
 *   - SP: 1h reporting delay + 47h conversion finalisation = 48h.
 *   - SB / SD: 3h reporting delay + 48h conversion finalisation + 24h headroom
 *     for clock-skew / late corrections = 72h.
 *   - DSP: 12h reporting delay + 48h conversion finalisation + 24h headroom = 84h.
 */
export const AMAZON_ADS_FINALISATION_WINDOW_HOURS: Record<AmazonAdsAdProduct, number> = {
  SPONSORED_PRODUCTS: 48,
  SPONSORED_BRANDS: 72,
  SPONSORED_DISPLAY: 72,
  DSP: 84,
};

/**
 * Story 19.16 Task 1.3 — adProduct slug used in the KV cache key. Matches the
 * `_bmad/bmm/data/integration-metrics/amazon-ads.md` slug convention. Adding
 * the `dsp` slug PRESERVES every existing 19.14/19.15 SP/SB/SD cache entry —
 * 19.16 is purely additive on the cache key shape.
 */
export const AMAZON_ADS_AD_PRODUCT_SLUG: Record<AmazonAdsAdProduct, "sp" | "sb" | "sd" | "dsp"> = {
  SPONSORED_PRODUCTS: "sp",
  SPONSORED_BRANDS: "sb",
  SPONSORED_DISPLAY: "sd",
  DSP: "dsp",
};

/**
 * Returns the appropriate cache TTL based on data freshness.
 *
 * Carry-forward 19.14 — preserved for SP-only callers. Prefer
 * `computeAmazonAdsCacheTtl` for ad-product-aware behaviour.
 *
 *   - Range entirely within last 48 hours of "now" → 1 hour
 *   - Range entirely older than 48 hours → 24 hours
 *   - Mixed → 1 hour (conservative)
 *
 * @deprecated Use `computeAmazonAdsCacheTtl(range, "SPONSORED_PRODUCTS")`.
 */
export function computeAmazonAdsSpCacheTtl(
  range: { startDate: Date; endDate: Date },
  now: Date = new Date()
): number {
  return computeAmazonAdsCacheTtl(range, "SPONSORED_PRODUCTS", now);
}

/**
 * Story 19.15 AC #10 — multi-product cache TTL. The window boundary is per
 * ad-product:
 *
 *   - SP: 48h finalization window (carry-forward 19.14).
 *   - SB / SD: 72h finalization window (3h reporting delay + 48h conversion
 *     finalization + 24h headroom).
 *
 * Within the window → fresh (1h TTL); outside → finalized (24h TTL); mixed →
 * conservative 1h.
 */
export function computeAmazonAdsCacheTtl(
  range: { startDate: Date; endDate: Date },
  adProduct: AmazonAdsAdProduct,
  now: Date = new Date()
): number {
  const windowHours = AMAZON_ADS_FINALISATION_WINDOW_HOURS[adProduct];
  const windowAgo = now.getTime() - windowHours * 60 * 60 * 1000;
  const startMs = range.startDate.getTime();
  const endMs = range.endDate.getTime();
  // Range entirely older than the window → finalized.
  if (endMs < windowAgo) return AMAZON_ADS_SP_CACHE_TTL_FINALIZED_SEC;
  // Range entirely within the window → fresh.
  if (startMs >= windowAgo) return AMAZON_ADS_SP_CACHE_TTL_FRESH_SEC;
  // Mixed → conservative.
  return AMAZON_ADS_SP_CACHE_TTL_FRESH_SEC;
}

// ============================================================================
// Cache key (AC #10 — Round 3 Vuln 4)
// ============================================================================

/**
 * Builds the per-tenant cache key for SP. Carry-forward 19.14 — preserved for
 * SP-only callers; prefer `buildAmazonAdsCacheKey` for new code.
 *
 * @deprecated Use `buildAmazonAdsCacheKey({ ...args, adProduct: "SPONSORED_PRODUCTS" })`.
 */
export function buildAmazonAdsSpCacheKey(args: {
  tenantId: string;
  integrationId: string;
  requestHash: string;
}): string {
  return buildAmazonAdsCacheKey({ ...args, adProduct: "SPONSORED_PRODUCTS" });
}

/**
 * Story 19.15 AC #10 — multi-product cache key.
 *
 * Shape: `amazon-ads:{slug}:{tenantId}:{integrationId}:{requestHash}` where
 * `slug` is `sp` / `sb` / `sd`. Existing 19.14 SP entries continue to be
 * addressable because the SP slug is preserved verbatim.
 *
 * **tenantId is non-optional and validated non-empty** because omitting it
 * would create cross-tenant cache poisoning, the highest-blast-radius failure
 * mode for Story 19.14 (Round 3 Red Team Vuln 4 + Round 5 First Principles Q2)
 * and carries forward unchanged in 19.15.
 */
export function buildAmazonAdsCacheKey(args: {
  adProduct: AmazonAdsAdProduct;
  tenantId: string;
  integrationId: string;
  requestHash: string;
}): string {
  if (typeof args.tenantId !== "string" || args.tenantId.length === 0) {
    throw new Error(
      "buildAmazonAdsCacheKey: tenantId is required (cross-tenant cache isolation)"
    );
  }
  if (typeof args.integrationId !== "string" || args.integrationId.length === 0) {
    throw new Error("buildAmazonAdsCacheKey: integrationId is required");
  }
  if (typeof args.requestHash !== "string" || args.requestHash.length === 0) {
    throw new Error("buildAmazonAdsCacheKey: requestHash is required");
  }
  const slug = AMAZON_ADS_AD_PRODUCT_SLUG[args.adProduct];
  if (!slug) {
    // Defense-in-depth — an unknown adProduct (e.g. via `as any`) at runtime.
    // Compile-time, the discriminated union prevents this. At runtime, fail
    // loud instead of building a malformed key.
    throw new Error(
      `buildAmazonAdsCacheKey: unsupported adProduct "${String(args.adProduct)}"`
    );
  }
  return `amazon-ads:${slug}:${args.tenantId}:${args.integrationId}:${args.requestHash}`;
}

// ============================================================================
// Story 19.15 — multi-product discriminated union (AC #1, #2, #11)
// ============================================================================

/**
 * Story 19.16 Task 1.4 — exhaustive union of all valid (adProduct, reportTypeId)
 * pairs across SP/SB/SD/DSP. The `_exhaustive: never` switch in
 * `pickAmazonAdsReportType` ensures adding a 5th surface (e.g. `"AMC"`) fails
 * typecheck if the developer forgets to add the case.
 *
 * **Naming-convention divergence preserved verbatim:** SP/SB/SD use lowerCamelCase
 * report-type IDs (`spCampaigns` / `sbCampaigns` / `sdCampaigns`) per Amazon's
 * v3 API spec, while DSP uses ALL_CAPS (`DSP_CAMPAIGN` / `DSP_INVENTORY` etc.)
 * mirroring Amazon's DSP API `type` field convention. The mapping to the wire-
 * format `type` field happens in `buildAmazonAdsReportRequest` — internally we
 * keep the `DSP_` prefix for grep-ability across the codebase.
 */
export type AmazonAdsReportTypeId =
  | SpReportTypeId
  | SbReportTypeId
  | SdReportTypeId
  | DspReportTypeId;

/**
 * Story 19.15 AC #1 — typed-discriminated successor to 19.14's `pickReportType`.
 * Routes a (adProduct, dimensions) tuple to the correct report-type ID per
 * surface. Pure function — no IO, no side effects.
 *
 * @example
 *   pickAmazonAdsReportType("SPONSORED_BRANDS", ["purchasedAsin"])
 *     → { ok: true, adProduct: "SPONSORED_BRANDS", reportTypeId: "sbPurchasedProduct" }
 *   pickAmazonAdsReportType("SPONSORED_BRANDS", ["promotedAsin"])
 *     → { ok: false, error: "ad_product_dimension_mismatch", ... }
 */
export type PickAmazonAdsReportTypeResult =
  | {
      readonly ok: true;
      readonly adProduct: AmazonAdsAdProduct;
      readonly reportTypeId: AmazonAdsReportTypeId;
      readonly warnings: ReadonlyArray<string>;
    }
  | {
      readonly ok: false;
      readonly error:
        | "unsupported_dimension_combination"
        | "ad_product_dimension_mismatch";
      readonly message: string;
    };

export function pickAmazonAdsReportType(
  adProduct: AmazonAdsAdProduct,
  dimensions: ReadonlyArray<string>
): PickAmazonAdsReportTypeResult {
  switch (adProduct) {
    case "SPONSORED_PRODUCTS": {
      const r = pickReportType(dimensions);
      if (!r.ok) {
        return { ok: false, error: r.error, message: r.message };
      }
      return {
        ok: true,
        adProduct: "SPONSORED_PRODUCTS",
        reportTypeId: r.reportTypeId,
        warnings: r.warnings,
      };
    }
    case "SPONSORED_BRANDS": {
      const r = pickSbReportType(dimensions);
      if (!r.ok) {
        return { ok: false, error: r.error, message: r.message };
      }
      return {
        ok: true,
        adProduct: "SPONSORED_BRANDS",
        reportTypeId: r.reportTypeId,
        warnings: r.warnings,
      };
    }
    case "SPONSORED_DISPLAY": {
      const r = pickSdReportType(dimensions);
      if (!r.ok) {
        return { ok: false, error: r.error, message: r.message };
      }
      return {
        ok: true,
        adProduct: "SPONSORED_DISPLAY",
        reportTypeId: r.reportTypeId,
        warnings: r.warnings,
      };
    }
    case "DSP": {
      // Story 19.16 AC #1 — DSP routing. `pickDspReportType` is pure and
      // returns one of `{DSP_CAMPAIGN, DSP_INVENTORY, DSP_GEOGRAPHY, DSP_PRODUCT}`
      // based on the requested dimensions, or an error for invalid combinations
      // (e.g. mixing PRODUCT + GEOGRAPHY dimensions which Amazon doesn't
      // support per its DSP report-types schema).
      const r = pickDspReportType(dimensions);
      if (!r.ok) {
        return { ok: false, error: r.error, message: r.message };
      }
      return {
        ok: true,
        adProduct: "DSP",
        reportTypeId: r.reportTypeId,
        warnings: r.warnings,
      };
    }
    default: {
      // TypeScript exhaustiveness gate per AC #1 — adding a 5th surface like
      // `"AMC"` (Amazon Marketing Cloud) without a case here will fail
      // typecheck with `Type '"AMC"' is not assignable to type 'never'`.
      const _exhaustive: never = adProduct;
      throw new Error(
        `pickAmazonAdsReportType: unhandled adProduct "${String(_exhaustive)}"`
      );
    }
  }
}

// ============================================================================
// Story 19.15 — submitAmazonAdsReport rename (AC #2)
// ============================================================================

/**
 * Story 19.15 AC #2 — generalized submit that accepts any of SP/SB/SD bodies.
 * The function body is byte-identical to `submitSponsoredProductsReport` (the
 * v3 content-type + endpoint + scope is uniform across SP/SB/SD); the rename
 * is purely for clarity. The deprecated alias is preserved for one story to
 * avoid breaking 19.14 in-tree callers; it lands removed in 19.17.
 *
 * **AC #11 runtime defense (defense-in-depth):** every submit validates the
 * (adProduct, reportTypeId) pair matches one of the 9 allowed pairs (SP 4 +
 * SB 3 + SD 2) BEFORE issuing the network call. Mismatches throw with the
 * documented error string and emit `amazon_ads_adapter_logic_bug_blocked`.
 * Compile-time, the discriminated union prevents the mismatch via
 * `buildAmazonAdsReportRequest`; runtime catches `as any` cheating.
 */
export async function submitAmazonAdsReport(
  args: {
    readonly accessToken: string;
    readonly clientId: string;
    /**
     * For SP/SB/SD: the sponsored-ads `profileId` from `accountInfo.id`.
     * For DSP: the `dspAccountId` (also from `accountInfo.id` of an
     * agency-typed profile per Story 19.16 AC #3) — this is the URL path
     * parameter for `/accounts/{dspAccountId}/dsp/reports/`. Reusing the
     * same field keeps the existing 19.14/19.15 call sites unchanged.
     */
    readonly profileId: string;
    readonly region: AmazonAdsRegion;
    readonly body: AnyAmazonAdsReportRequestBody;
  },
  fetchImpl: typeof fetch = fetch
): Promise<{ readonly reportId: string; readonly status: string }> {
  // Story 19.16 Task 5.6 / AC #2 — discriminated dispatch on body shape.
  // DSP bodies have NO `configuration` sub-object; that single shape
  // difference is the dispatch discriminator.
  if (isDspReportRequestBody(args.body)) {
    return submitAmazonAdsDspReport(args as Parameters<typeof submitAmazonAdsDspReport>[0], fetchImpl);
  }

  // AC #11 runtime defense — validate the discriminated tuple BEFORE issuing
  // the request. Throws with the documented error message if mismatched.
  validateAdProductReportTypePair(
    args.body.configuration.adProduct,
    args.body.configuration.reportTypeId,
    "submitAmazonAdsReport"
  );

  const url = `${AMAZON_ADS_REGIONAL_HOSTS[args.region]}/reporting/reports`;
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    AMAZON_ADS_SP_CONTROL_TIMEOUT_MS
  );
  try {
    const res = await fetchImpl(url, {
      method: "POST",
      headers: {
        Authorization: `bearer ${args.accessToken}`,
        "Amazon-Advertising-API-ClientId": args.clientId,
        "Amazon-Advertising-API-Scope": args.profileId,
        "Content-Type": AMAZON_ADS_REPORT_API_CONTENT_TYPE,
        Accept: AMAZON_ADS_REPORT_API_CONTENT_TYPE,
      },
      body: JSON.stringify(args.body),
      redirect: "manual",
      signal: controller.signal,
    });
    if (!res.ok) {
      let txt: string | undefined;
      try {
        txt = await res.text();
      } catch {
        txt = undefined;
      }
      throw new SpReportApiError(
        res.status,
        `submit report: HTTP ${res.status}`,
        txt
      );
    }
    const json = (await res.json()) as { reportId?: string; status?: string };
    if (!json.reportId) {
      throw new SpReportApiError(0, "submit report: missing reportId in response");
    }
    return {
      reportId: json.reportId,
      status: json.status ?? "PENDING",
    };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Story 19.16 Task 5.6 / AC #2 — DSP-specific submit. Diverges from SP/SB/SD
 * in 4 ways:
 *
 *   1. **URL path**: `/accounts/{dspAccountId}/dsp/reports/` (trailing slash
 *      REQUIRED — practitioners report 404 on the no-trailing-slash variant
 *      per Discussion #15).
 *   2. **Content-Type + Accept**: `application/vnd.dspcreatereports.v3+json`
 *      on BOTH headers — mismatched headers return 415 per Discussion #15.
 *   3. **`Amazon-Advertising-API-Scope` header**: DSP scoping is primarily the
 *      URL path parameter (`accountInfo.id` of an agency-typed profile), but
 *      Amazon's gateway expects the Scope header on every Ads API request.
 *      We send it set to the same `dspAccountId` value for symmetry with
 *      SP/SB/SD calls — sending it is HARMLESS (Amazon's API ignores
 *      header-vs-path mismatches in favor of the URL path on DSP routes per
 *      Discussion #15).
 *   4. **Status enum**: response uses `IN_PROGRESS` / `SUCCESS` (NOT SP/SB/SD's
 *      `PENDING` / `IN_QUEUE` / `PROCESSING` / `COMPLETED`).
 *
 * Defense: emits `amazon_ads_dsp_advertiser_id_omitted` when `advertiserIds`
 * is intentionally not sent (advertiser-scoped account) — forensic trail for
 * future scoping debates.
 */
export async function submitAmazonAdsDspReport(
  args: {
    readonly accessToken: string;
    readonly clientId: string;
    readonly profileId: string; // dspAccountId
    readonly region: AmazonAdsRegion;
    readonly body: DspReportRequestBody;
  },
  fetchImpl: typeof fetch = fetch
): Promise<{ readonly reportId: string; readonly status: string }> {
  // Round 3 review fix — validate dspAccountId is non-empty BEFORE building
  // the URL. Empty profileId would produce `/accounts//dsp/reports/` which
  // Amazon would 404 with a non-actionable error. Throw loud with a
  // diagnostic message instead.
  if (typeof args.profileId !== "string" || args.profileId.length === 0) {
    throw new SpReportApiError(
      0,
      "submitAmazonAdsDspReport: dspAccountId (profileId) is required — check pickDspAccountId result"
    );
  }
  // AC #2 / Task 4 runtime defense — validate the wire-format `type` field
  // maps back to a known DspReportTypeId.
  const reportTypeId = reverseDspWireType(args.body.type);
  validateAdProductReportTypePair("DSP", reportTypeId, "submitAmazonAdsDspReport");

  // Forensic log when advertiserIds is omitted. The caller controls whether
  // advertiserIds are sent — empty means EITHER advertiser-scoped account
  // (correct) OR an agency-scoped account where the caller didn't populate
  // advertiserIds (potential bug). Forensic trail; profileType is "unknown"
  // because submit cannot distinguish — `pickDspAccountId` knows the type
  // and the upstream callsite is responsible for the population decision.
  if (!args.body.advertiserIds || args.body.advertiserIds.length === 0) {
    console.log(
      JSON.stringify({
        event: "amazon_ads_dsp_advertiser_id_omitted",
        profileType: "unknown",
        reason:
          "advertiserIds omitted at submit — caller's decision; either advertiser-scoped account or agency-scoped account without populated advertiserIds",
        timestamp: new Date().toISOString(),
      })
    );
  }

  const url = `${AMAZON_ADS_REGIONAL_HOSTS[args.region]}/accounts/${encodeURIComponent(args.profileId)}/dsp/reports/`;
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    AMAZON_ADS_SP_CONTROL_TIMEOUT_MS
  );
  try {
    const res = await fetchImpl(url, {
      method: "POST",
      headers: {
        Authorization: `bearer ${args.accessToken}`,
        "Amazon-Advertising-API-ClientId": args.clientId,
        "Amazon-Advertising-API-Scope": args.profileId,
        "Content-Type": AMAZON_ADS_DSP_REPORT_API_CONTENT_TYPE,
        Accept: AMAZON_ADS_DSP_REPORT_API_CONTENT_TYPE,
      },
      body: JSON.stringify(args.body),
      redirect: "manual",
      signal: controller.signal,
    });
    if (!res.ok) {
      let txt: string | undefined;
      try {
        txt = await res.text();
      } catch {
        txt = undefined;
      }
      // Story 19.16 AC #6 — 406 forensic log (signals scoping / advertiserIds bug).
      if (res.status === 406) {
        console.log(
          JSON.stringify({
            event: "amazon_ads_dsp_406_observed",
            scopingDecision: "agency",
            advertiserIdsSent:
              !!args.body.advertiserIds && args.body.advertiserIds.length > 0,
            timestamp: new Date().toISOString(),
          })
        );
      }
      throw new SpReportApiError(
        res.status,
        `submit DSP report: HTTP ${res.status}`,
        txt
      );
    }
    const json = (await res.json()) as { reportId?: string; status?: string };
    if (!json.reportId) {
      throw new SpReportApiError(
        0,
        "submit DSP report: missing reportId in response"
      );
    }
    return {
      reportId: json.reportId,
      // DSP defaults to IN_PROGRESS when status is omitted (matches its enum).
      status: json.status ?? "IN_PROGRESS",
    };
  } finally {
    clearTimeout(timeout);
  }
}

function reverseDspWireType(
  wireType: DspReportRequestBody["type"]
): DspReportTypeId {
  switch (wireType) {
    case "CAMPAIGN":
      return "DSP_CAMPAIGN";
    case "INVENTORY":
      return "DSP_INVENTORY";
    case "GEOGRAPHY":
      return "DSP_GEOGRAPHY";
    case "PRODUCT":
      return "DSP_PRODUCT";
    // Story 19.16.1 — 3 NEW wire types.
    case "AUDIENCE":
      return "DSP_AUDIENCE";
    case "TECHNICAL":
      return "DSP_TECHNICAL";
    case "AUDIO_VIDEO":
      return "DSP_AUDIO_VIDEO";
    default: {
      const _exhaustive: never = wireType;
      throw new Error(
        `reverseDspWireType: unhandled wireType "${String(_exhaustive)}"`
      );
    }
  }
}

// ============================================================================
// Story 19.16 Task 5.4 / AC #10 — DSP record parser
// ============================================================================

/**
 * Story 19.16 Task 5.4 / AC #10 — parses one DSP record from a flat JSON array.
 *
 * Validates:
 *   - the input is a JSON object (rejects array elements / null / primitives),
 *   - every numeric field passes `Number.isFinite()` (Round 3 Vuln 3 carry-
 *     forward — DSP returns currency values as strings in some legacy
 *     responses; the guard rejects `"Infinity"` / `"NaN"` / `"123abc"`).
 *
 * Returns a discriminated result mirroring `parseSpNdjsonRecord`'s shape so
 * the downstream `runAmazonAdsReportPipeline` can stay product-agnostic about
 * record-level malformedness.
 */
export type DspParseRecordResult =
  | { readonly _malformed: true; readonly reason: string }
  | { readonly _malformed: false; readonly record: SpReportRecord };

export function parseDspJsonRecord(record: unknown): DspParseRecordResult {
  if (record === null || typeof record !== "object" || Array.isArray(record)) {
    return { _malformed: true, reason: "non-object record" };
  }
  const obj = record as Record<string, unknown>;
  // Round 3 Vuln 3 — Number.isFinite() guard against Amazon string-injection.
  // Coerce string-numerics to number where appropriate; reject "Infinity" /
  // "NaN" / "123abc" outright.
  const out: SpReportRecord = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === "number") {
      if (!Number.isFinite(v)) {
        return {
          _malformed: true,
          reason: `field ${k} non-finite number`,
        };
      }
      out[k] = v;
      continue;
    }
    if (typeof v === "string") {
      // Pass strings through unchanged — caller decides whether to coerce.
      // The XSS / cache-poisoning defense in `validateCachePayload` catches
      // <script> / javascript: payloads downstream.
      out[k] = v;
      continue;
    }
    if (typeof v === "boolean" || v === null) {
      out[k] = v;
      continue;
    }
    // Nested objects/arrays are rare in DSP reports but allowed defensively.
    out[k] = v;
  }
  return { _malformed: false, record: out };
}

// ============================================================================
// Story 19.16 Task 5.5 / AC #10 — DSP downloader
// ============================================================================

export interface DownloadAndParseDspResult {
  readonly records: SpReportRecord[];
  readonly warnings: string[];
  readonly bytesProcessed: number;
}

/**
 * Story 19.16 Task 5.5 / AC #10 — DSP report downloader.
 *
 * IDENTICAL contract to `downloadAndParseReport` (the SP/SB/SD function from
 * 19.14) EXCEPT:
 *
 *   - **No gzip decompression** — DSP returns flat JSON arrays uncompressed.
 *   - **`JSON.parse(text)` + `Array.isArray` validation** — rejects malformed
 *     non-array responses with `amazon_ads_dsp_malformed_response` log.
 *   - **`parseDspJsonRecord` per element** — rejects non-object array entries
 *     with `amazon_ads_dsp_malformed_record` log.
 *   - **50MB raw-byte cap** — same numerical limit as SP/SB/SD's decompressed
 *     cap; DSP doesn't gzip so the cap maps to ~50MB raw JSON (~250k records
 *     typical, well above the 100k AMAZON_ADS_SP_CACHE_MAX_RECORDS cap).
 *   - **SSRF allowlist via `isSafeS3DownloadUrl(url, "DSP")`** — uses the
 *     tighter `corvo-reports.s3*.amazonaws.com` regex.
 */
export async function downloadAndParseDspReport(
  url: string,
  fetchImpl: typeof fetch = fetch
): Promise<DownloadAndParseDspResult> {
  // AC #10 — DSP-specific SSRF allowlist (corvo-reports bucket only).
  if (!isSafeS3DownloadUrl(url, "DSP")) {
    throw new Error("suspicious_download_url");
  }

  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    AMAZON_ADS_SP_DOWNLOAD_TIMEOUT_MS
  );
  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: "GET",
      redirect: "manual",
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    throw new SpReportApiError(res.status, `S3 DSP download HTTP ${res.status}`);
  }
  if (!res.body) {
    throw new Error("S3 DSP response missing body");
  }

  // Stream the raw text (no gzip) with a 50MB raw-byte cap.
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let bytesProcessed = 0;
  let text = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (!value) continue;
      bytesProcessed += value.byteLength;
      if (bytesProcessed > AMAZON_ADS_SP_MAX_DECOMPRESSED_BYTES) {
        try {
          reader.releaseLock();
        } catch {
          // best-effort
        }
        throw new Error("report_oversized");
      }
      text += decoder.decode(value, { stream: true });
    }
    // Flush trailing bytes from the decoder.
    text += decoder.decode();
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "amazon_ads_dsp_malformed_response",
        reason:
          err instanceof Error
            ? `JSON parse error: ${err.message}`
            : "JSON parse error",
        timestamp: new Date().toISOString(),
      })
    );
    throw new SpReportApiError(0, "DSP report response is not valid JSON");
  }

  if (!Array.isArray(parsed)) {
    console.log(
      JSON.stringify({
        event: "amazon_ads_dsp_malformed_response",
        reason: "not_array",
        timestamp: new Date().toISOString(),
      })
    );
    throw new SpReportApiError(0, "DSP report response is not a JSON array");
  }

  const records: SpReportRecord[] = [];
  const warnings: string[] = [];
  let malformedCount = 0;
  for (let i = 0; i < parsed.length; i++) {
    const r = parseDspJsonRecord(parsed[i]);
    if (r._malformed) {
      malformedCount++;
      console.log(
        JSON.stringify({
          event: "amazon_ads_dsp_malformed_record",
          recordIndex: i,
          reason: r.reason,
          timestamp: new Date().toISOString(),
        })
      );
      continue;
    }
    records.push(r.record);
  }
  if (malformedCount > 0) {
    warnings.push(
      `Skipped ${malformedCount} malformed DSP JSON record(s) — adapter is malformed-record-tolerant per Story 19.16 AC #10`
    );
  }

  return { records, warnings, bytesProcessed };
}

// ============================================================================
// Story 19.15 AC #11 — buildAmazonAdsReportRequest discriminated union
// ============================================================================

/**
 * Story 19.15 AC #11 — `application/vnd.createasyncreportrequest.v3+json`
 * request body, discriminated on `adProduct`. Compile-time enforces the
 * (adProduct, reportTypeId) match — assigning `reportTypeId: "sbCampaigns"`
 * with `adProduct: "SPONSORED_PRODUCTS"` is a TypeScript error.
 *
 * **Why this matters at runtime, not just compile time:** Amazon's v3 API is
 * LENIENT — sending an SP request with SB columns may 200-respond with empty
 * records rather than 400-rejecting. That would silently zero customer
 * dashboards. The runtime guard in `validateAdProductReportTypePair` catches
 * `as any` cheating + future refactor mistakes BEFORE the network call.
 */
export interface SbReportRequestBody {
  readonly name: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly configuration: {
    readonly adProduct: "SPONSORED_BRANDS";
    readonly reportTypeId: SbReportTypeId;
    readonly groupBy: ReadonlyArray<string>;
    readonly columns: ReadonlyArray<string>;
    readonly timeUnit: "DAILY" | "SUMMARY";
    readonly format: "GZIP_JSON";
  };
}

export interface SdReportRequestBody {
  readonly name: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly configuration: {
    readonly adProduct: "SPONSORED_DISPLAY";
    readonly reportTypeId: SdReportTypeId;
    readonly groupBy: ReadonlyArray<string>;
    readonly columns: ReadonlyArray<string>;
    readonly timeUnit: "DAILY" | "SUMMARY";
    readonly format: "GZIP_JSON";
  };
}

/**
 * Story 19.16 Task 3.1 / AC #2 — DSP request body. Fundamentally divergent
 * shape from SP/SB/SD:
 *
 *   - **NO `configuration` sub-object** — fields are hoisted to the top level.
 *   - **`type` field replaces `adProduct` + `reportTypeId`** — the wire-format
 *     `type` is one of `CAMPAIGN`/`INVENTORY`/`GEOGRAPHY`/`PRODUCT` (without
 *     the `DSP_` prefix used internally for grep-ability).
 *   - **`format: "JSON"`** (NOT `"GZIP_JSON"`) — DSP returns a flat JSON array,
 *     uncompressed.
 *   - **`dimensions` + `metrics` are top-level arrays** — the `groupBy` /
 *     `columns` split from SP/SB/SD does not apply.
 *   - **`advertiserIds` is OPTIONAL** — populated only when the `dspAccountId`
 *     path parameter is an entity (agency-typed) ID; OMITTED when the
 *     dspAccountId is itself an advertiser ID (advertiser-typed profile).
 *     Sending `advertiserIds` on an advertiser-scoped account returns 406 per
 *     amzn/ads-advanced-tools-docs Discussion #15.
 *   - **NOTE on path parameter:** the DSP report submission URL is
 *     `POST /accounts/{dspAccountId}/dsp/reports/` — `dspAccountId` is NOT in
 *     the body; it's derived from `accountInfo.id` of an agency-typed profile
 *     via `pickDspAccountId` (Story 19.16 AC #3).
 */
export interface DspReportRequestBody {
  /**
   * Story 19.16.1 — extended to 7 wire-format report types.
   *   - 19.16: CAMPAIGN / INVENTORY / GEOGRAPHY / PRODUCT
   *   - 19.16.1: AUDIENCE / TECHNICAL / AUDIO_VIDEO (BETA)
   */
  readonly type:
    | "CAMPAIGN"
    | "INVENTORY"
    | "GEOGRAPHY"
    | "PRODUCT"
    | "AUDIENCE"
    | "TECHNICAL"
    | "AUDIO_VIDEO";
  readonly startDate: string;
  readonly endDate: string;
  readonly format: "JSON";
  readonly dimensions: ReadonlyArray<string>;
  readonly metrics: ReadonlyArray<string>;
  readonly timeUnit?: "SUMMARY" | "DAILY";
  readonly advertiserIds?: ReadonlyArray<string>;
}

// ============================================================================
// Story 19.16.1 AC #11 — 2026 Attribution-Change Baseline Columns
// ============================================================================

/**
 * Story 19.16.1 AC #11 — Amazon's January 1, 2026 attribution change retired
 * the legacy 14-day blind view-through model and explicitly recommended the
 * `Purchases (All Views)` columns for cross-2026-comparison-baseline
 * historical-comparability for QBR + finance reporting.
 *
 * **UTC-only** per Round 2 Pre-mortem B — timezone-naïve callers MUST convert
 * to UTC before comparison. Documented in JSDoc; tests cover the 4-case
 * matrix (pre-only / cross / post-only / exactly-on-boundary).
 */
export const AMAZON_ADS_2026_ATTRIBUTION_BOUNDARY_MS = Date.UTC(2026, 0, 1, 0, 0, 0);

/**
 * Story 19.16.1 AC #11 — `*AllViews*` baseline columns per ad-product.
 * Emitted ONLY when the requested time range crosses the 2026-01-01 UTC
 * boundary.
 */
export const AMAZON_ADS_ALLVIEWS_COLUMNS_BY_PRODUCT: Record<
  AmazonAdsAdProduct,
  ReadonlyArray<string>
> = {
  SPONSORED_PRODUCTS: [
    "purchasesAllViews7d",
    "salesAllViews7d",
    "purchasesAllViews14d",
    "salesAllViews14d",
  ],
  SPONSORED_BRANDS: ["purchasesAllViews14d", "salesAllViews14d"],
  SPONSORED_DISPLAY: ["purchasesAllViews14d", "salesAllViews14d"],
  DSP: [
    "purchasesAllViews14d",
    "salesAllViews14d",
    "totalPurchasesAllViews14d",
    "totalSalesAllViews14d",
    "newToBrandPurchasesAllViews14d",
    "newToBrandSalesAllViews14d",
  ],
};

/**
 * Story 19.16.1 AC #11 / Task 16.2 — UTC-only cross-boundary detection.
 *
 * @param range - the requested time range. Caller is responsible for UTC
 *   conversion; this function compares `getTime()` UTC milliseconds only.
 * @returns `true` IFF the range straddles 2026-01-01 UTC (start < boundary
 *   AND end >= boundary). Pre-only, post-only, and exactly-on-boundary
 *   ranges return `false` (no baseline emitted).
 */
export function shouldEmitAllViewsBaseline(range: {
  readonly startDate: Date;
  readonly endDate: Date;
}): boolean {
  return (
    range.startDate.getTime() < AMAZON_ADS_2026_ATTRIBUTION_BOUNDARY_MS &&
    range.endDate.getTime() >= AMAZON_ADS_2026_ATTRIBUTION_BOUNDARY_MS
  );
}

/**
 * Story 19.16.1 AC #11 — convenience helper. Returns the baseline columns to
 * inject into the request when crossing the boundary; returns empty array
 * otherwise. Always emits `amazon_ads_2026_attribution_baseline_emitted` log
 * when columns are actually added so operators can grep Logpush.
 */
export function getAllViewsBaselineColumns(
  adProduct: AmazonAdsAdProduct,
  range: { readonly startDate: Date; readonly endDate: Date }
): ReadonlyArray<string> {
  if (!shouldEmitAllViewsBaseline(range)) return [];
  const cols = AMAZON_ADS_ALLVIEWS_COLUMNS_BY_PRODUCT[adProduct];
  console.log(
    JSON.stringify({
      event: "amazon_ads_2026_attribution_baseline_emitted",
      adProduct,
      rangeStartUtc: range.startDate.toISOString(),
      rangeEndUtc: range.endDate.toISOString(),
      baselineColumnsAdded: cols,
      timestamp: new Date().toISOString(),
    })
  );
  return cols;
}

/**
 * Story 19.16 / 19.16.1 — internal `DspReportTypeId` ↔ wire-format `type`
 * mapping. Extended to 7 entries in 19.16.1.
 */
export const AMAZON_ADS_DSP_TYPE_BY_REPORT_TYPE_ID: Record<
  DspReportTypeId,
  DspReportRequestBody["type"]
> = {
  DSP_CAMPAIGN: "CAMPAIGN",
  DSP_INVENTORY: "INVENTORY",
  DSP_GEOGRAPHY: "GEOGRAPHY",
  DSP_PRODUCT: "PRODUCT",
  DSP_AUDIENCE: "AUDIENCE",
  DSP_TECHNICAL: "TECHNICAL",
  DSP_AUDIO_VIDEO: "AUDIO_VIDEO",
};

/**
 * Story 19.16 Task 3.2 — widened union to include DSP. Existing 19.14/19.15
 * SP/SB/SD callers continue to type-check unchanged.
 */
export type AnyAmazonAdsReportRequestBody =
  | SpReportRequestBody
  | SbReportRequestBody
  | SdReportRequestBody
  | DspReportRequestBody;

/**
 * Story 19.16 — runtime discriminator. SP/SB/SD bodies have a `configuration`
 * sub-object; DSP bodies do NOT. This single shape difference is what
 * `submitAmazonAdsReport` dispatches on to route DSP requests to the
 * `/dsp/reports/` endpoint with the DSP content-type.
 */
export function isDspReportRequestBody(
  body: AnyAmazonAdsReportRequestBody
): body is DspReportRequestBody {
  return !("configuration" in body);
}

// ============================================================================
// Story 19.16 Task 6 / AC #11 — per-product status dispatch tables
// ============================================================================

/**
 * Story 19.16 AC #11 — terminal-success status set, per ad-product.
 *
 *   - SP / SB / SD: `["COMPLETED", "SUCCESS"]` (carry-forward 19.14 — `SUCCESS`
 *     is a v2-alias defensively accepted alongside v3's `COMPLETED`).
 *   - DSP: `["SUCCESS"]` only — DSP never returns `COMPLETED` per the Python
 *     Amazon Ad API SDK docs.
 *
 * `runAmazonAdsReportPipeline` accepts an optional `terminalSuccessStatuses`
 * override; when omitted, the orchestration falls back to the SP-defaulted
 * constants for back-compat with 19.14/19.15 callers.
 */
export const AMAZON_ADS_TERMINAL_SUCCESS_STATUSES_BY_PRODUCT: Record<
  AmazonAdsAdProduct,
  ReadonlyArray<string>
> = {
  SPONSORED_PRODUCTS: ["COMPLETED", "SUCCESS"],
  SPONSORED_BRANDS: ["COMPLETED", "SUCCESS"],
  SPONSORED_DISPLAY: ["COMPLETED", "SUCCESS"],
  DSP: ["SUCCESS"],
};

/**
 * Story 19.16 AC #11 — pending status set, per ad-product.
 *
 *   - SP / SB / SD: `["PENDING", "IN_QUEUE", "PROCESSING"]` (carry-forward 19.14).
 *   - DSP: `["IN_PROGRESS"]` only — DSP's status enum diverges per the Python
 *     Amazon Ad API SDK docs.
 *
 * Round 4 QA Gap 3 — forgetting to override these tables when calling the
 * pipeline with `adProduct: "DSP"` would silently use SP's pending statuses,
 * causing 30s-budget timeouts on every DSP fetch (the loop never matches a
 * terminal status). `runAmazonAdsReportPipeline` THROWS when DSP is supplied
 * without explicit overrides — kills the slow-rot failure mode.
 */
export const AMAZON_ADS_PENDING_STATUSES_BY_PRODUCT: Record<
  AmazonAdsAdProduct,
  ReadonlyArray<string>
> = {
  SPONSORED_PRODUCTS: ["PENDING", "IN_QUEUE", "PROCESSING"],
  SPONSORED_BRANDS: ["PENDING", "IN_QUEUE", "PROCESSING"],
  SPONSORED_DISPLAY: ["PENDING", "IN_QUEUE", "PROCESSING"],
  DSP: ["IN_PROGRESS"],
};

export interface BuildAmazonAdsReportRequestArgs {
  readonly adProduct: AmazonAdsAdProduct;
  readonly reportTypeId: AmazonAdsReportTypeId;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly metrics: ReadonlyArray<string>;
  readonly dimensions: ReadonlyArray<string>;
  readonly timeUnit?: "DAILY" | "SUMMARY";
  readonly requestHashShort?: string;
  /**
   * Story 19.16 AC #2 — DSP-only — populated when the chosen DSP profile is
   * agency-typed (entity-scoped). Omitted when the chosen profile is
   * advertiser-scoped (sending `advertiserIds` on advertiser-scoped accounts
   * returns HTTP 406 per Discussion #15). Caller derives this from
   * `pickDspAccountId(profilesAcrossRegions)` and the persisted
   * `account_metadata.dspAdvertisers.advertiserIds` JSON field.
   */
  readonly advertiserIds?: ReadonlyArray<string>;
}

/**
 * AC #11 — builds the v3 request body for ANY of SP/SB/SD. The discriminated
 * union ensures (adProduct, reportTypeId) match at the type level; the runtime
 * `validateAdProductReportTypePair` defends against `as any` bypass.
 */
export function buildAmazonAdsReportRequest(
  args: BuildAmazonAdsReportRequestArgs
): { body: AnyAmazonAdsReportRequestBody; warnings: ReadonlyArray<string> } {
  // AC #11 runtime defense — fail loud BEFORE constructing the body.
  validateAdProductReportTypePair(
    args.adProduct,
    args.reportTypeId,
    "buildAmazonAdsReportRequest"
  );

  const startDate = isoDate(args.startDate);
  const endDate = isoDate(args.endDate);
  const slug = AMAZON_ADS_AD_PRODUCT_SLUG[args.adProduct];
  const name = `${slug}-${args.reportTypeId}-${startDate}-${endDate}-${args.requestHashShort ?? "noh"}`;

  // Story 19.16.1 AC #11 — inject `*AllViews*` baseline columns when the
  // requested time range crosses 2026-01-01 UTC. Boundary detection is
  // UTC-only (Round 2 Pre-mortem B). Pre-only / post-only / exactly-on-
  // boundary ranges return empty arrays and emit no log.
  const allViewsBaseline = getAllViewsBaselineColumns(args.adProduct, {
    startDate: args.startDate,
    endDate: args.endDate,
  });
  // Append baseline keys to the requested metric set. Caller's per-product
  // column-map lookup will resolve them to wire-format column names; unknown
  // keys surface as warnings (acceptable — pre-launch detection of typos).
  const metricsWithBaseline =
    allViewsBaseline.length > 0
      ? [...args.metrics, ...allViewsBaseline]
      : args.metrics;

  switch (args.adProduct) {
    case "SPONSORED_PRODUCTS": {
      const { columns, warnings } = buildColumnList(
        args.reportTypeId as SpReportTypeId,
        metricsWithBaseline,
        args.dimensions
      );
      const groupBy = buildGroupByList(
        args.reportTypeId as SpReportTypeId,
        args.dimensions
      );
      const body: SpReportRequestBody = {
        name,
        startDate,
        endDate,
        configuration: {
          adProduct: "SPONSORED_PRODUCTS",
          reportTypeId: args.reportTypeId as SpReportTypeId,
          groupBy,
          columns,
          timeUnit: args.timeUnit ?? "DAILY",
          format: "GZIP_JSON",
        },
      };
      return { body, warnings };
    }
    case "SPONSORED_BRANDS": {
      const { columns, warnings } = buildSbColumnList(
        args.reportTypeId as SbReportTypeId,
        metricsWithBaseline,
        args.dimensions
      );
      const groupBy = buildSbGroupByList(
        args.reportTypeId as SbReportTypeId,
        args.dimensions
      );
      const body: SbReportRequestBody = {
        name,
        startDate,
        endDate,
        configuration: {
          adProduct: "SPONSORED_BRANDS",
          reportTypeId: args.reportTypeId as SbReportTypeId,
          groupBy,
          columns,
          timeUnit: args.timeUnit ?? "DAILY",
          format: "GZIP_JSON",
        },
      };
      return { body, warnings };
    }
    case "SPONSORED_DISPLAY": {
      const { columns, warnings } = buildSdColumnList(
        args.reportTypeId as SdReportTypeId,
        metricsWithBaseline,
        args.dimensions
      );
      const groupBy = buildSdGroupByList(
        args.reportTypeId as SdReportTypeId,
        args.dimensions
      );
      const body: SdReportRequestBody = {
        name,
        startDate,
        endDate,
        configuration: {
          adProduct: "SPONSORED_DISPLAY",
          reportTypeId: args.reportTypeId as SdReportTypeId,
          groupBy,
          columns,
          timeUnit: args.timeUnit ?? "DAILY",
          format: "GZIP_JSON",
        },
      };
      return { body, warnings };
    }
    case "DSP": {
      // Story 19.16 Task 3 / AC #2 — DSP body shape diverges fundamentally:
      // top-level `type` + `dimensions[]` + `metrics[]` + `format: "JSON"`,
      // NO `configuration` sub-object, optional `advertiserIds`. The internal
      // `DspReportTypeId` (e.g. `DSP_CAMPAIGN`) maps to the wire-format
      // `type` (e.g. `CAMPAIGN`) via `AMAZON_ADS_DSP_TYPE_BY_REPORT_TYPE_ID`.
      const reportTypeId = args.reportTypeId as DspReportTypeId;
      const { columns, warnings } = buildDspColumnList(
        reportTypeId,
        metricsWithBaseline,
        args.dimensions
      );
      const groupBy = buildDspGroupByList(reportTypeId, args.dimensions);
      const wireType = AMAZON_ADS_DSP_TYPE_BY_REPORT_TYPE_ID[reportTypeId];
      const body: DspReportRequestBody = {
        type: wireType,
        startDate,
        endDate,
        format: "JSON",
        // For DSP: dimensions and metrics are top-level arrays. We mirror the
        // SP/SB/SD shape semantically: `dimensions` carries the breakdown
        // axis (groupBy) and `metrics` carries the measure columns.
        dimensions: groupBy,
        metrics: columns,
        timeUnit: args.timeUnit ?? "DAILY",
        ...(args.advertiserIds !== undefined && args.advertiserIds.length > 0
          ? { advertiserIds: args.advertiserIds }
          : {}),
      };
      return { body, warnings };
    }
    default: {
      const _exhaustive: never = args.adProduct;
      throw new Error(
        `buildAmazonAdsReportRequest: unhandled adProduct "${String(_exhaustive)}"`
      );
    }
  }
}

/**
 * AC #11 runtime defense — throws with the documented error message + emits
 * `amazon_ads_adapter_logic_bug_blocked` structured-log if the (adProduct,
 * reportTypeId) pair is invalid. Caller can grep Sentry for the substring
 * `Adapter logic bug: adProduct + reportTypeId mismatch — see Story 19.15 AC #11`.
 */
export function validateAdProductReportTypePair(
  adProduct: AmazonAdsAdProduct,
  reportTypeId: AmazonAdsReportTypeId,
  callsite: string
): void {
  // Story 19.16 Task 4 / AC #2, #11 — extended with DSP. The exhaustive
  // switch surfaces compile-time errors when a 5th ad-product is added
  // without updating this guard.
  //
  // Round 1 review fix: an unknown `adProduct` (e.g. via `as any` cast at
  // the call site) MUST emit the same `amazon_ads_adapter_logic_bug_blocked`
  // structured-log + same "Adapter logic bug" Error message as the
  // wrong-(adProduct, reportTypeId)-pair branch — so Grafana alerts grep on
  // a single event name regardless of which axis was tampered with.
  let allowed: ReadonlyArray<string> | null;
  switch (adProduct) {
    case "SPONSORED_PRODUCTS":
      allowed = SP_REPORT_TYPE_IDS;
      break;
    case "SPONSORED_BRANDS":
      allowed = SB_REPORT_TYPE_IDS;
      break;
    case "SPONSORED_DISPLAY":
      allowed = SD_REPORT_TYPE_IDS;
      break;
    case "DSP":
      allowed = DSP_REPORT_TYPE_IDS;
      break;
    default: {
      // Compile-time exhaustiveness — adding a 5th surface fails typecheck.
      // Runtime: route the unknown-adProduct case through the same alert path.
      const _exhaustive: never = adProduct;
      void _exhaustive;
      allowed = null;
    }
  }
  if (allowed === null || !allowed.includes(reportTypeId)) {
    // Structured log — Grafana alert fires on this event.
    console.log(
      JSON.stringify({
        event: "amazon_ads_adapter_logic_bug_blocked",
        adProduct,
        reportTypeId,
        callsite,
        timestamp: new Date().toISOString(),
      })
    );
    throw new Error(
      `Adapter logic bug: adProduct + reportTypeId mismatch — see Story 19.15 AC #11 (got adProduct="${String(adProduct)}" reportTypeId="${String(reportTypeId)}" at ${callsite})`
    );
  }
}

// ============================================================================
// Story 19.15 — multi-product NDJSON parser (AC #9, Round 3 Vuln 3)
// ============================================================================

/**
 * Story 19.15 AC #9 — generalized NDJSON record parser. Carries forward the SP
 * malformed-line tolerance pattern; callers wrap with `Number.isFinite()` per
 * Round 3 Vuln 3 when extracting numeric metric values.
 *
 * Renamed from `parseSpNdjsonRecord` per AC #9; deprecated alias preserved.
 */
export function parseAmazonAdsNdjsonRecord(line: string): SpParseLineResult {
  return parseSpNdjsonRecord(line);
}
